/*
 * AdminCEOChallenge — Admin dashboard for the Youth CEO Camp Final Challenge.
 * Sections:
 *   1. KPIs (attempts, students, avg score, passing %, avg time)
 *   2. Quiz settings (toggle timer, speed bonus, leaderboard, passing score, max attempts)
 *   3. Most-missed questions + category performance
 *   4. Recent completed attempts with reset/delete + CSV export
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Download, RefreshCcw, Trash2, ChevronLeft, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminCEOChallenge() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const loadAll = async () => {
    try {
      const [s, c, r] = await Promise.all([
        axios.get(`${API}/admin/ceo-challenge/stats`),
        axios.get(`${API}/admin/ceo-challenge/config`),
        axios.get(`${API}/admin/ceo-challenge/results?limit=100`),
      ]);
      setStats(s.data);
      setConfig(c.data);
      setResults(r.data.results || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/ceo-challenge/config`, {
        timer_enabled: config.timer_enabled,
        time_per_question: Number(config.time_per_question),
        speed_bonus_enabled: config.speed_bonus_enabled,
        leaderboard_enabled: config.leaderboard_enabled,
        leaderboard_show_full_names: config.leaderboard_show_full_names,
        passing_score: Number(config.passing_score),
        max_attempts: Number(config.max_attempts),
      });
      setConfig(res.data);
      setToast("Settings saved");
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setToast(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetAttempt = async (attemptId) => {
    if (!window.confirm("Delete this attempt and allow the student to retake?")) return;
    await axios.post(`${API}/admin/ceo-challenge/attempts/${attemptId}/reset`);
    await loadAll();
  };

  const deleteAttempt = async (attemptId) => {
    if (!window.confirm("Permanently delete this attempt?")) return;
    await axios.delete(`${API}/admin/ceo-challenge/attempts/${attemptId}`);
    await loadAll();
  };

  const resetLeaderboard = async () => {
    if (!window.confirm("Wipe ALL CEO Challenge attempts, responses and badges? This cannot be undone.")) return;
    await axios.post(`${API}/admin/ceo-challenge/leaderboard/reset`);
    await loadAll();
  };

  const exportCsv = () => {
    const token = localStorage.getItem("token");
    fetch(`${BACKEND}/api/admin/ceo-challenge/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "ceo-challenge-results.csv";
        link.click();
      });
  };

  return (
    <div className="min-h-screen bg-[#0B1E3F] text-white" data-testid="admin-ceo-root">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button
          onClick={() => nav("/admin")}
          className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-bold mb-4"
        >
          <ChevronLeft className="w-4 h-4" /> Admin
        </button>

        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[#FFD500]">Youth CEO Camp</p>
            <h1 className="text-3xl sm:text-4xl font-black">Final Challenge — Admin</h1>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-ceo-export"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 rounded-full px-4 py-2 text-sm font-bold"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              data-testid="admin-ceo-reset-leaderboard"
              onClick={resetLeaderboard}
              className="inline-flex items-center gap-2 bg-[#F87171]/15 hover:bg-[#F87171]/25 ring-1 ring-[#F87171]/40 text-[#FCA5A5] rounded-full px-4 py-2 text-sm font-bold"
            >
              <Trash2 className="w-4 h-4" /> Reset All
            </button>
          </div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="admin-ceo-kpis">
          <Kpi label="Attempts" value={stats?.total_attempts ?? "—"} />
          <Kpi label="Unique Students" value={stats?.unique_students ?? "—"} />
          <Kpi label="Avg Score" value={stats?.average_score ?? "—"} />
          <Kpi label="Passing %" value={stats ? `${stats.passing_percentage}%` : "—"} />
          <Kpi label="Avg Time" value={stats ? `${Math.round(stats.average_completion_seconds)}s` : "—"} />
        </div>

        {/* Settings */}
        {config && (
          <section className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-6 mb-8" data-testid="admin-ceo-settings">
            <h2 className="text-xl font-black mb-4">Quiz Settings</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Toggle label="Timer enabled" checked={!!config.timer_enabled} onChange={(v) => setConfig({ ...config, timer_enabled: v })} testId="admin-ceo-timer-toggle" />
              <Toggle label="Speed bonus enabled" checked={!!config.speed_bonus_enabled} onChange={(v) => setConfig({ ...config, speed_bonus_enabled: v })} testId="admin-ceo-speed-toggle" />
              <Toggle label="Leaderboard enabled" checked={!!config.leaderboard_enabled} onChange={(v) => setConfig({ ...config, leaderboard_enabled: v })} testId="admin-ceo-leaderboard-toggle" />
              <Toggle label="Show full names on leaderboard" checked={!!config.leaderboard_show_full_names} onChange={(v) => setConfig({ ...config, leaderboard_show_full_names: v })} testId="admin-ceo-fullname-toggle" />
              <NumberInput label="Seconds per question" value={config.time_per_question} min={5} max={120} onChange={(v) => setConfig({ ...config, time_per_question: v })} testId="admin-ceo-time-input" />
              <NumberInput label="Passing score (correct)" value={config.passing_score} min={1} max={20} onChange={(v) => setConfig({ ...config, passing_score: v })} testId="admin-ceo-passing-input" />
              <NumberInput label="Max attempts per student" value={config.max_attempts} min={1} max={20} onChange={(v) => setConfig({ ...config, max_attempts: v })} testId="admin-ceo-attempts-input" />
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                data-testid="admin-ceo-save-settings"
                onClick={saveConfig}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-[#FFD500] text-black font-black rounded-full px-6 py-3 hover:bg-white disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
              </button>
              {toast && <span className="text-sm text-[#86EFAC] font-bold">{toast}</span>}
            </div>
          </section>
        )}

        {/* Insights: most-missed + categories */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <section className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-6" data-testid="admin-ceo-missed">
            <h2 className="text-xl font-black mb-4">Most Missed Questions</h2>
            {(!stats?.most_missed || stats.most_missed.length === 0) && (
              <p className="text-white/60 text-sm">No misses yet.</p>
            )}
            <ul className="space-y-2">
              {stats?.most_missed?.map((m) => (
                <li key={m.question_number} className="flex gap-3 items-start">
                  <span className="shrink-0 w-8 h-8 rounded-lg bg-[#F87171]/15 text-[#FCA5A5] font-black flex items-center justify-center text-sm">
                    Q{m.question_number}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-bold leading-snug">{m.question_text}</p>
                    <p className="text-xs text-white/50 mt-0.5">{m.misses} misses</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-6" data-testid="admin-ceo-cat-perf">
            <h2 className="text-xl font-black mb-4">Category Performance</h2>
            {(!stats?.category_performance || stats.category_performance.length === 0) && (
              <p className="text-white/60 text-sm">No data yet.</p>
            )}
            <div className="space-y-3">
              {stats?.category_performance?.map((c) => (
                <div key={c.key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-bold">{c.label}</span>
                    <span className="text-white/70">
                      {c.correct}/{c.total} · {c.percentage}%
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-[#FFD500]" style={{ width: `${c.percentage}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Recent completed attempts */}
        <section className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-6" data-testid="admin-ceo-results-table">
          <h2 className="text-xl font-black mb-4">Recent Completed Attempts</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/60 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2 pr-3">Student</th>
                  <th className="text-left py-2 pr-3">Team</th>
                  <th className="text-right py-2 pr-3">Score</th>
                  <th className="text-right py-2 pr-3">Correct</th>
                  <th className="text-right py-2 pr-3">Time</th>
                  <th className="text-left py-2 pr-3">Level</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {results.length === 0 && (
                  <tr><td colSpan="7" className="py-4 text-white/60">No completed attempts yet.</td></tr>
                )}
                {results.map((r) => (
                  <tr key={r.id}>
                    <td className="py-2.5 pr-3 font-bold">{r.student_name}</td>
                    <td className="py-2.5 pr-3 text-white/70">{r.team_name || "—"}</td>
                    <td className="py-2.5 pr-3 text-right font-black">{r.score}</td>
                    <td className="py-2.5 pr-3 text-right">{r.correct_count}/20</td>
                    <td className="py-2.5 pr-3 text-right">{r.completion_seconds}s</td>
                    <td className="py-2.5 pr-3">
                      <span className="text-xs font-bold uppercase text-[#FFD500]">
                        {r.achievement_level || "—"}
                      </span>
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button
                        onClick={() => resetAttempt(r.id)}
                        className="text-xs font-bold text-[#7DD3FC] hover:text-white mr-3"
                        data-testid={`admin-ceo-reset-${r.id}`}
                      >
                        <RefreshCcw className="inline w-3 h-3 mr-1" /> Reset
                      </button>
                      <button
                        onClick={() => deleteAttempt(r.id)}
                        className="text-xs font-bold text-[#FCA5A5] hover:text-white"
                        data-testid={`admin-ceo-delete-${r.id}`}
                      >
                        <Trash2 className="inline w-3 h-3 mr-1" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
      <p className="text-[10px] font-black uppercase tracking-widest text-white/60">{label}</p>
      <p className="mt-1.5 text-2xl font-black">{value}</p>
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between bg-[#0B1E3F] rounded-xl px-4 py-3 ring-1 ring-white/10 cursor-pointer">
      <span className="text-sm font-bold">{label}</span>
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 accent-[#FFD500]"
      />
    </label>
  );
}

function NumberInput({ label, value, min, max, onChange, testId }) {
  return (
    <label className="flex flex-col bg-[#0B1E3F] rounded-xl px-4 py-3 ring-1 ring-white/10">
      <span className="text-xs font-bold text-white/60 mb-1">{label}</span>
      <input
        type="number"
        data-testid={testId}
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="bg-transparent text-white text-lg font-black focus:outline-none"
      />
    </label>
  );
}
